import { normalizeTimelineItems } from "./timeline";

export function mapStaffMarketplaceCategory(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    description: raw.description || "",
    parentId: raw.parent_id ?? null,
    parentName: raw.parent_name || null,
    sortOrder: raw.sort_order ?? 0,
    isActive: !!raw.is_active,
    productCount: raw.products_count ?? 0,
    createdByName: raw.created_by_name,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export function mapStaffMarketplaceProduct(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    sku: raw.sku,
    categoryId: raw.category_id,
    categoryName: raw.category_name,
    subcategoryId: raw.subcategory_id,
    subcategoryName: raw.subcategory_name,
    price: Number(raw.price) || 0,
    priceNgn: Number(raw.price) || 0,
    currency: raw.currency || "NGN",
    unitLabel: raw.unit_label,
    stockQuantity: raw.stock_quantity,
    stockStatus: raw.stock_status,
    lifecycleStatus: raw.lifecycle_status,
    status: raw.lifecycle_status,
    trackInventory: !!raw.track_inventory,
    preOrderAllowed: !!raw.pre_order_allowed,
    coverImage: raw.cover_image,
    publishedAt: raw.published_at,
    updatedAt: raw.updated_at,
    createdByName: raw.created_by_name,
    shortDescription: raw.short_description,
    fullDescription: raw.full_description,
    sellerName: raw.seller_name,
    minimumQuantity: raw.minimum_quantity ?? 1,
    maximumQuantity: raw.maximum_quantity,
    lowStockThreshold: raw.low_stock_threshold,
    deliveryAvailable: !!raw.delivery_available,
    pickupAvailable: !!raw.pickup_available,
    pickupLocation: raw.pickup_location || (Array.isArray(raw.pickup_locations) ? raw.pickup_locations[0] : "") || "",
    pickupLocations: Array.isArray(raw.pickup_locations)
      ? raw.pickup_locations.filter((l) => String(l || "").trim())
      : raw.pickup_location
        ? [raw.pickup_location]
        : [],
    deliveryRegions: raw.delivery_regions || [],
    estimatedFulfillmentTime: raw.estimated_fulfillment_time,
    publicContent: raw.public_content || {},
    media: raw.media || [],
    scheduledPublishAt: raw.scheduled_publish_at,
  };
}

export function mapStaffMarketplaceOrder(raw) {
  if (!raw) return null;
  const items = (raw.items || []).map((item) => ({
    id: item.id,
    productId: item.product_id,
    productName: item.product_name,
    sku: item.sku,
    unitLabel: item.unit_label,
    quantity: item.quantity,
    unitPrice: Number(item.unit_price) || 0,
    lineTotal: Number(item.line_total) || 0,
  }));

  const timeline = normalizeTimelineItems(raw.timeline);

  return {
    id: raw.id,
    orderNumber: raw.order_number,
    status: raw.status,
    paymentStatus: raw.payment_status,
    checkoutMode: raw.checkout_mode,
    accountType: raw.account_type,
    applicantId: raw.applicant_id,
    applicantName: raw.applicant_name || raw.full_name,
    customerName: raw.full_name,
    customerType: raw.account_type === "member" ? "cooperative_member" : "standalone_investor",
    phone: raw.phone,
    email: raw.email,
    contactMethod: raw.contact_method,
    customerNote: raw.customer_note,
    staffNotes: raw.staff_notes,
    deliveryMethod: raw.delivery_method,
    deliveryAddress: raw.delivery_address,
    pickupLocation: raw.pickup_location,
    subtotal: Number(raw.subtotal) || 0,
    deliveryFee: Number(raw.delivery_fee) || 0,
    total: Number(raw.total_amount) || 0,
    totalAmount: Number(raw.total_amount) || 0,
    currency: raw.currency || "NGN",
    itemsCount: raw.items_count ?? items.length,
    items,
    stockReserved: !!raw.stock_reserved,
    stockHoldActive: !!raw.stock_hold_active,
    stockHoldExpiresAt: raw.stock_hold_expires_at ?? null,
    paymentReference: raw.payment_reference ?? null,
    paymentAmount: Number(raw.payment_amount) || null,
    paymentFee: Number(raw.payment_fee) || null,
    paymentTotal: Number(raw.payment_total) || null,
    paymentConfirmedAt: raw.payment_confirmed_at ?? null,
    paymentFailureReason: raw.payment_failure_reason ?? null,
    assignedStaffId: raw.assigned_staff_id,
    assignedStaffName: raw.assigned_staff_name,
    paymentUrl: raw.payment_url,
    onlinePaymentAvailable: raw.online_payment_available === true,
    submittedAt: raw.submitted_at,
    updatedAt: raw.updated_at,
    confirmedAt: raw.confirmed_at,
    processingStartedAt: raw.processing_started_at,
    readyAt: raw.ready_at,
    shippedAt: raw.shipped_at,
    deliveredAt: raw.delivered_at,
    completedAt: raw.completed_at,
    declinedAt: raw.declined_at,
    cancelledAt: raw.cancelled_at,
    timeline,
  };
}

export function mapStaffMarketplaceDashboard(raw) {
  if (!raw) return null;
  return {
    totalProducts: raw.total_products ?? 0,
    publishedProducts: raw.published_products ?? 0,
    lowStockProducts: raw.low_stock_products ?? 0,
    outOfStockProducts: raw.out_of_stock_products ?? 0,
    submittedOrders: raw.submitted_orders ?? 0,
    ordersAwaitingConfirmation: raw.orders_awaiting_confirmation ?? 0,
    processingOrders: raw.processing_orders ?? 0,
    completedOrderValue: Number(raw.completed_order_value) || 0,
    orderVolume: raw.order_volume || {},
    bestPerforming: (raw.best_performing_products || []).map((p) => ({
      productId: p.product_id,
      productName: p.product_name,
      unitsSold: p.units_sold,
      revenue: Number(p.revenue) || 0,
    })),
    inventoryAlerts: (raw.inventory_alerts || []).map((a) => ({
      productId: a.product_id,
      name: a.name,
      slug: a.slug,
      stockQuantity: a.stock_quantity,
      stockStatus: a.stock_status,
      lifecycleStatus: a.lifecycle_status,
    })),
    recentOrders: (raw.recent_orders || []).map((o) => ({
      id: o.id,
      orderNumber: o.order_number,
      status: o.status,
      checkoutMode: o.checkout_mode,
      totalAmount: Number(o.total_amount) || 0,
      currency: o.currency,
      customerName: o.customer_name,
      at: o.at,
    })),
    recentActivity: (raw.recent_activity || []).map((a) => ({
      type: a.type,
      orderId: a.order_id,
      fromStatus: a.from_status,
      toStatus: a.to_status,
      note: a.note,
      changedByType: a.changed_by_type,
      at: a.at,
    })),
  };
}

export function productToApiBody(form) {
  const body = {
    name: form.name,
    category_id: form.categoryId || form.category_id,
    subcategory_id: form.subcategoryId || form.subcategory_id || null,
    sku: form.sku || null,
    short_description: form.shortDescription || form.short_description || null,
    full_description: form.fullDescription || form.full_description || null,
    seller_name: form.sellerName || form.seller_name || null,
    price: form.priceNgn ?? form.price,
    currency: form.currency || "NGN",
    unit_label: form.unitLabel || form.unit_label,
    minimum_quantity: form.minimumQuantity ?? form.minimum_quantity ?? 1,
    maximum_quantity: form.maximumQuantity ?? form.maximum_quantity ?? null,
    stock_quantity: form.stockQuantity ?? form.stock_quantity ?? null,
    low_stock_threshold: form.lowStockThreshold ?? form.low_stock_threshold ?? null,
    track_inventory: form.trackInventory ?? form.track_inventory ?? true,
    pre_order_allowed: form.preOrderAllowed ?? form.pre_order_allowed ?? false,
    delivery_available: form.deliveryAvailable ?? form.delivery_available ?? true,
    pickup_available: form.pickupAvailable ?? form.pickup_available ?? false,
    pickup_locations: (() => {
      const list = (form.pickupLocations || form.pickup_locations || [])
        .map((l) => String(l || "").trim())
        .filter(Boolean);
      if (list.length) return list;
      const single = form.pickupLocation || form.pickup_location;
      return single ? [String(single).trim()] : [];
    })(),
    estimated_fulfillment_time: form.estimatedFulfillmentTime || form.estimated_fulfillment_time || null,
    cover_image: form.coverImage || form.cover_image || null,
    scheduled_publish_at: form.scheduledPublishAt || form.scheduled_publish_at || null,
  };

  const deliveryRegions = form.deliveryRegions || form.delivery_regions;
  if (deliveryRegions != null) body.delivery_regions = deliveryRegions;

  const publicContent = form.publicContent || form.public_content;
  if (publicContent != null) body.public_content = publicContent;

  const media = form.media;
  if (media != null) body.media = media;

  return body;
}

/** Staff portal product detail/edit path — uses slug, not numeric id. */
export function staffMarketplaceProductPath(productOrSlug, suffix = "") {
  const slug =
    typeof productOrSlug === "string" || typeof productOrSlug === "number"
      ? String(productOrSlug)
      : productOrSlug?.slug;
  if (!slug) return "/super_admin/investor_portal/marketplace/products";
  const base = `/super_admin/investor_portal/marketplace/products/${slug}`;
  return suffix ? `${base}${suffix}` : base;
}
